import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { Select } from '../atoms/Select';
import { Checkbox } from '../atoms/Checkbox';
import { FormField } from '../atoms/FormField';
import { DataManagementSection } from './DataManagementSection';
import { settingsSchema, type SettingsFormData } from '../../schemas/settingsSchema';
import type { GlobalSettings } from '../../types/settings';

interface SettingsFormProps {
  settings: GlobalSettings;
  onSettingsChange: (settings: GlobalSettings) => void;
  isFullPage?: boolean;
}

export const SettingsForm: React.FC<SettingsFormProps> = ({ settings, onSettingsChange, isFullPage = false }) => {
  const [isOpen, setIsOpen] = useState(isFullPage);
  const [showDataManagement] = useState(true);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: settings,
  });

  // Watch form values for conditional rendering
  const tariffType = watch('electricityCosts.tariffType');
  const solarEnabled = watch('solarPanels.enabled');
  const bncRate = watch('bncRate');

  const onSubmit = (data: SettingsFormData) => {
    onSettingsChange(data);
    if (!isFullPage) {
      setIsOpen(false);
    }
  };

  const declarationFrequencyOptions = [
    { value: 'mensuel', label: 'Mensuel' },
    { value: 'trimestriel', label: 'Trimestriel' },
    { value: 'annuel', label: 'Annuel' },
  ];

  const tariffTypeOptions = [
    { value: 'simple', label: 'Tarif simple' },
    { value: 'hc_hp', label: 'Heures Creuses / Heures Pleines' },
  ];

  if (!isOpen && !isFullPage) {
    return (
      <div className="max-w-6xl mx-auto mb-8">
        <Button onClick={() => setIsOpen(true)} variant="secondary" className="flex items-center">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Configuration globale
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto mb-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Configuration globale
                  </h2>
                  <p className="text-purple-100 text-sm mt-1">
                    Paramètres de déclaration et coûts énergétiques
                  </p>
                </div>
              </div>
              {!isFullPage && (
                <Button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  variant="secondary"
                  className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              )}
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Section Déclaration */}
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                Fréquence de déclaration
              </h3>
              <FormField
                label="Périodicité des déclarations BNC"
                htmlFor="declarationFrequency"
                error={errors.declarationFrequency?.message}
              >
                <Select
                  id="declarationFrequency"
                  {...register('declarationFrequency')}
                  options={declarationFrequencyOptions}
                  className="transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-blue-500"
                />
              </FormField>
            </div>

            {/* Section Coûts électriques */}
            <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                Coûts électriques
              </h3>

              <div className="space-y-4">
                <FormField
                  label="Type de facturation"
                  htmlFor="tariffType"
                  error={errors.electricityCosts?.tariffType?.message}
                >
                  <Select
                    id="tariffType"
                    {...register('electricityCosts.tariffType')}
                    options={tariffTypeOptions}
                    className="transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-yellow-500"
                  />
                </FormField>

                {tariffType === 'simple' && (
                  <FormField
                    label="Tarif électrique (€/kWh)"
                    htmlFor="simpleTariff"
                    error={errors.electricityCosts?.simpleTariff?.message}
                  >
                    <Input
                      id="simpleTariff"
                      type="number"
                      step="0.0001"
                      min="0"
                      {...register('electricityCosts.simpleTariff', { valueAsNumber: true })}
                      placeholder="Ex: 0.1740"
                      className="transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-yellow-500"
                    />
                  </FormField>
                )}

                {tariffType === 'hc_hp' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Tarif Heures Creuses (€/kWh)"
                      htmlFor="hcTariff"
                      error={errors.electricityCosts?.hcTariff?.message}
                    >
                      <Input
                        id="hcTariff"
                        type="number"
                        step="0.0001"
                        min="0"
                        {...register('electricityCosts.hcTariff', { valueAsNumber: true })}
                        placeholder="Ex: 0.1470"
                        className="transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-yellow-500"
                      />
                    </FormField>
                    <FormField
                      label="Tarif Heures Pleines (€/kWh)"
                      htmlFor="hpTariff"
                      error={errors.electricityCosts?.hpTariff?.message}
                    >
                      <Input
                        id="hpTariff"
                        type="number"
                        step="0.0001"
                        min="0"
                        {...register('electricityCosts.hpTariff', { valueAsNumber: true })}
                        placeholder="Ex: 0.1841"
                        className="transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-yellow-500"
                      />
                    </FormField>
                  </div>
                )}
              </div>
            </div>

            {/* Section Fiscalité BNC */}
            <div className="bg-red-50 rounded-lg p-6 border border-red-200">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                Fiscalité et charges
              </h3>

              <div className="space-y-4">
                <FormField
                  label="Taux BNC personnalisé (%)"
                  htmlFor="bncRate"
                  hint="Charges sociales + impôts sur revenus BNC (généralement 34%)"
                  error={errors.bncRate?.message}
                >
                  <Input
                    id="bncRate"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={(bncRate * 100).toFixed(1)}
                    onChange={(e) => setValue('bncRate', parseFloat(e.target.value) / 100 || 0)}
                    placeholder="34"
                    className="transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-red-500"
                  />
                </FormField>
                <div className="text-xs text-red-600">
                  💡 Exemple : 34% = charges sociales (environ 25%) + impôts sur le revenu (environ 9%)
                </div>
              </div>
            </div>

            {/* Section Panneaux solaires */}
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                Panneaux solaires
              </h3>

              <div className="space-y-4">
                <FormField
                  label=""
                  htmlFor="solarEnabled"
                >
                  <Checkbox
                    id="solarEnabled"
                    {...register('solarPanels.enabled')}
                    checked={solarEnabled}
                    onChange={(e) => setValue('solarPanels.enabled', e.target.checked)}
                    label="Installation de panneaux solaires"
                  />
                </FormField>

                {solarEnabled && (
                  <FormField
                    label="Production moyenne par jour (W)"
                    htmlFor="solarProduction"
                    error={errors.solarPanels?.averageDailyProduction?.message}
                  >
                    <Input
                      id="solarProduction"
                      type="number"
                      step="1"
                      min="0"
                      {...register('solarPanels.averageDailyProduction', { valueAsNumber: true })}
                      placeholder="Ex: 3500"
                      className="transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-green-500"
                    />
                  </FormField>
                )}
              </div>
            </div>

            {/* Section Gestion des données */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <div className="w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                    />
                  </svg>
                </div>
                Gestion de vos données
              </h3>
              <DataManagementSection showDataManagement={showDataManagement} />
            </div>

            {/* Boutons */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              {!isFullPage && (
                <Button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  variant="secondary"
                >
                  Annuler
                </Button>
              )}
              <Button
                type="submit"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Sauvegarder
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
